<script setup lang="ts">
import { MediaProps } from '~/components/cms/Media/Media.vue'
import { ActionFields, actionRenderGuards } from '~/components/cms/Action/actionHelpers'

export type CardProps = {
  action?: ActionFields & { sfccPromotion?: boolean }
  media?: MediaProps
  title?: string
  text?: string
  variant: 'two-up' | 'three-up' | 'four-up' | 'six-up' | 'teaser' | 'basicTeaserTile' | 'product'
  preset?: 'two-up' | 'two-up-wide' | 'three-up' | 'three-up-wide' | 'four-up' | 'six-up' /* for image resizing */
}
const props = withDefaults(defineProps<CardProps>(), {})
const variantClass = props.variant ? `card--${props.variant}` : ''

const shouldRenderMediaAction = computed(() => {
  const { isBlank, isButton, isSfccPromotion, isProductList, isRTPModal } = actionRenderGuards(props.action)
  if (isBlank) return false

  return (
    props.variant === 'two-up' ||
    props.variant === 'three-up' ||
    props.variant === 'four-up' ||
    props.variant === 'six-up' ||
    isSfccPromotion ||
    isButton ||
    isRTPModal ||
    isProductList
  )
})
</script>

<template>
  <article class="card" data-testid="card" :class="variantClass">
    <div class="card__media" v-if="media">
      <Action v-if="shouldRenderMediaAction" v-bind="action">
        <Media v-bind="media" :preset="preset" />
      </Action>
      <Media v-else v-bind="media" :preset="preset" />
    </div>
    <div v-if="title || text || action" class="card__content">
      <h3 class="card__title" data-testid="card__title" v-if="title">{{ title }}</h3>
      <Markdown v-if="text" class="card__text" :content="text" />
      <Action
        class="card__action"
        data-testid="card__action"
        v-if="action"
        v-bind="action"
        appearance="Button"
        :button="{
          variant: ['teaser', 'product'].includes(variant)
            ? 'secondary-reverse'
            : variant === 'basicTeaserTile'
              ? 'secondary'
              : 'tertiary',
        }"
      />
    </div>
  </article>
</template>

<style lang="postcss">
.card {
  --media-aspect-ratio: var(--aspect-ratio-4-5);
  --media-aspect-ratio-desktop: var(--aspect-ratio-5-4);
  --hover-scale: 1.05;

  position: relative;

  &:not(.card--product):not(.card--teaser):not(.card--basicTeaserTile) {
    .card__action {
      display: unset;
    }
  }

  .card__media {
    overflow: hidden;
    aspect-ratio: var(--media-aspect-ratio);

    /* ensures aspect ratio and object fit takes control of image sizing and all media will always just fill as much space as it can otherwise */
    > *,
    .action-wrapper,
    .action,
    .picture-container,
    .picture,
    .picture-link,
    .video {
      height: 100%;
      width: 100%;
    }

    .picture__img,
    .video__video {
      aspect-ratio: var(--media-aspect-ratio);
      height: 100%;
      object-fit: cover;
      transition: var(--transition-ease-out-1);
      width: 100%;
    }

    @media (--desktop) {
      aspect-ratio: var(--media-aspect-ratio-desktop);

      .picture__img,
      .video__video {
        aspect-ratio: var(--media-aspect-ratio-desktop);
      }
    }
  }

  .card__text {
    max-width: var(--text-max-width); /* 416px */
    text-wrap: pretty;

    img {
      width: auto;
    }
  }

  /* Zoom image animation on Card hover */
  &:has(.card__action:hover),
  &:has(.picture__img:hover),
  &:has(.video__video:hover) {
    .picture__img,
    .video__video {
      scale: var(--hover-scale);
    }
  }
}

.card--product,
.card--teaser {
  position: relative;

  > * {
    color: var(--text-reverse);
  }
  .card__content {
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.8));
    bottom: 0;
    left: 0;
    padding: var(--space-70);
    position: absolute;
    width: 100%;
    .card__title {
      margin-bottom: var(--space-50);
    }
    .card__text {
      margin-bottom: var(--space-70);
    }
  }
}

.card--product {
  width: 100%;
  .card__media {
    display: flex;
    height: 100%;
    width: 100%;
    .picture__img,
    .video__video {
      height: 100%;
      @media (--tablet) {
        aspect-ratio: unset;
      }
    }
    > * {
      height: 100%;
      width: 100%;
    }
  }
  .card__action {
    display: flex;
    max-width: 100%;
    text-align: center;
    @media (--tablet) {
      &.button-secondary,
      &.button-secondary-reverse {
        min-width: unset;
      }
    }
  }
}

.card--teaser {
  height: 100%;
  .card__media {
    @media (--tablet) {
      aspect-ratio: var(--media-aspect-ratio-desktop);
      .picture__img,
      .video__video {
        aspect-ratio: var(--media-aspect-ratio-desktop);
      }
    }
  }
}

.card--basicTeaserTile {
  --media-aspect-ratio: var(--aspect-ratio-16-10);
  --media-aspect-ratio-desktop: var(--aspect-ratio-16-10);
  .card__content {
    padding: var(--space-70);
    .card__text {
      margin-bottom: var(--space-70);
    }
  }
}

.card--two-up,
.card--three-up,
.card--four-up,
.card--six-up {
  display: grid;
  place-content: stretch;
  grid-template-rows: auto 1fr;

  .card__title {
    margin-bottom: var(--space-50);
  }
  .card__text {
    margin-bottom: var(--space-60);
  }
}

.card--two-up {
  @media (--tablet) {
    .card__media {
      aspect-ratio: var(--media-aspect-ratio-desktop);
    }
  }
}

.card--two-up,
.card--three-up {
  @media (--mobile-only) {
    .card__media {
      width: 100vw; /* make smaller image to fill */
    }
  }

  .card__content {
    padding: var(--space-50);
    @media (--desktop) {
      padding: var(--space-60);
    }
  }
}

.card--four-up {
  --media-aspect-ratio-desktop: var(--aspect-ratio-4-5); /* Reset */

  .card__media {
    max-width: 18rem;
    background: var(--ui-neutral-20);

    @media (--tablet) {
      max-width: 19.3125rem; /* Avoid layout shift while loading */
    }

    @media (--desktop) {
      max-width: 19.5rem; /* Avoid layout shift while loading and fix gap at desktop */
    }
  }

  .card__content {
    margin-top: var(--space-50);

    /* support older implementation when action button is only for shop-by-style or shop-by-category, see ticket #213410 */
    &:not(:has(.card__title)):not(:has(.card__text)) {
      .button-tertiary {
        text-decoration: none;
      }
    }
  }
}

.card--six-up {
  --media-aspect-ratio-desktop: var(--aspect-ratio-4-5);
  --hover-scale: 1.1;

  .card__content {
    padding-block: var(--space-30);
    /* support older implementation when action button is only for shop-by-style or shop-by-category, see ticket #213410*/
    &:not(:has(.card__title)):not(:has(.card__text)) {
      .card__action {
        text-decoration: none;
      }
    }
  }

  .card__action {
    @media (--mobile-tablet-only) {
      font-size: var(--text-small);
    }
  }
}
</style>
